// Wires @scribe/core's platform-agnostic upload-queue FSM (UploadQueue, see
// packages/core/src/upload-queue/queue.ts) to this app's web Uploader + localStorage-backed
// metadata store, and exposes a small imperative API for screens (RecordScreen, and future
// file-upload screens) to enqueue a captured/selected audio Blob for multipart upload once
// the server has issued a presigned plan.
import { useEffect, useMemo, useState } from 'react';
import { UploadQueue } from '@scribe/core/upload-queue';
import type { UploadItem } from '@scribe/core/upload-queue';
import { useApi } from '../lib/ApiContext';
import { webUploadQueueStorage, registerUploadBlob, releaseUploadBlob } from '../lib/uploadStorage';
import { webUploader } from '../lib/uploader';
import { isMock } from '../lib/mock';
import type { CreateRecordingRequest } from '@scribe/core/types';

export interface UploadQueueHandle {
  items: UploadItem[];
  /**
   * Creates the Recording server-side (obtaining the presigned multipart plan), registers
   * the local Blob, and enqueues it for upload. Returns the new recording id immediately —
   * the actual byte upload proceeds in the background via the FSM.
   */
  enqueue: (
    blob: Blob,
    meta: Omit<CreateRecordingRequest, 'content_type' | 'part_count'>,
  ) => Promise<string>;
  retry: (itemId: string) => void;
  cancel: (itemId: string) => void;
}

// One part per ~8MB keeps individual PUTs small enough for a flaky connection to retry
// cheaply, while staying well under S3's 10,000-part multipart ceiling for any recording
// this app is likely to produce (mic captures / meeting recordings, not multi-GB files).
const TARGET_PART_SIZE_BYTES = 8 * 1024 * 1024;

// Module-level SINGLETON queue, shared by every useUploadQueue() caller. It MUST outlive any
// individual screen: RecordScreen enqueues an upload and immediately navigates away
// (onDone), so a queue owned by RecordScreen's own lifecycle would be stop()'d on unmount
// and the background PUTs would never run — which is exactly the bug where mic recordings
// were created server-side but no bytes ever uploaded. One app-lifetime queue means
// navigation can't abort an in-flight upload; the per-component hook only subscribes/
// unsubscribes its own view listener (never stops the queue).
let sharedQueue: UploadQueue | null = null;
let resumed = false;

function getSharedQueue(): UploadQueue {
  if (!sharedQueue) {
    sharedQueue = new UploadQueue({
      storage: webUploadQueueStorage,
      uploader: webUploader,
      // classifyError omitted — the package's defaultClassifyError (upload-queue/errors.ts)
      // already handles ScribeApiError/ScribeNetworkError plus generic {status}-shaped
      // errors, which is exactly what our Uploader throws (see lib/uploader.ts).
    });
  }
  return sharedQueue;
}

export function useUploadQueue(): UploadQueueHandle {
  const { client } = useApi();
  const [items, setItems] = useState<UploadItem[]>([]);

  const queue = useMemo(() => getSharedQueue(), []);

  useEffect(() => {
    const unsubscribe = queue.subscribe((event) => {
      setItems((prev) => {
        if (event.type === 'item-removed') return prev.filter((i) => i.id !== event.id);
        const idx = prev.findIndex((i) => i.id === event.item.id);
        if (idx === -1) return [...prev, event.item];
        const next = prev.slice();
        next[idx] = event.item;
        return next;
      });
    });
    // Resume mid-flight items from a previous session (reload during upload) exactly once
    // for the shared queue — not per component mount.
    if (!resumed) {
      resumed = true;
      void queue.resume();
    }
    // Deliberately do NOT queue.stop() here: the queue is app-lifetime (see sharedQueue).
    // Unmounting a screen only detaches its view listener.
    return unsubscribe;
  }, [queue]);

  return {
    items,
    async enqueue(blob, meta) {
      const partCount = Math.max(1, Math.ceil(blob.size / TARGET_PART_SIZE_BYTES));
      // MediaRecorder blobs carry a parameterized MIME like "audio/webm;codecs=opus".
      // Send only the base type ("audio/webm"): the backend maps content_type -> object-key
      // extension (audio/webm -> .webm) and AWS Transcribe infers the media format from that
      // extension. The ";codecs=..." parameter isn't in that map, so it produced an
      // extensionless key that Transcribe can't process. Bytes are unchanged; only the label.
      const contentType = ((blob.type || 'audio/webm').split(';')[0] ?? 'audio/webm')
        .trim()
        .toLowerCase();

      if (isMock()) {
        // No real server round-trip in mock mode; fabricate a recording id so screens can
        // still navigate to a "detail" view.
        return `rec-mock-${Date.now()}`;
      }

      const { recording, upload } = await client.createRecording({
        ...meta,
        content_type: contentType,
        part_count: partCount,
      });

      // Register the local audio bytes under the queue item's id *before* enqueueing so
      // the driver's first driveNextPart() call (which can fire synchronously via the
      // 0ms schedule) always finds them.
      registerUploadBlob(recording.id, blob);
      await queue.enqueue(
        { partCount, contentType, totalBytes: blob.size },
        recording.id,
        upload,
        recording.id,
      );
      return recording.id;
    },
    retry(itemId) {
      void queue.retry(itemId);
    },
    cancel(itemId) {
      void queue.cancel(itemId).then(() => releaseUploadBlob(itemId));
    },
  };
}
